<template>
  <div class="project-layout" :class="{ mobile: isMobile }">
    <aside class="sidebar" :class="{ open: isMobileMenuOpen }">
      <TreeMenu
        :frameworks="frameworks"
        :selectedItem="uiStore.selectedItem"
        :projectId="projectId"
        :manuscript="projectStore.manuscript"
        :costs="projectStore.costs"
        :isPending="projectStore.isPending"
        :isProcessing="projectStore.isProcessing"
        :isPublic="projectStore.isPublic"
        :isOwner="projectStore.isOwner"
        :devMode="debugMode"
        @toast="showToast"
        @recreate-started="handleRecreateStarted"
        @force-stopped="handleForceStop"
      />
    </aside>

    <div v-if="isMobile && isMobileMenuOpen" class="mobile-overlay" @click="closeMobileMenu"></div>

    <main class="content" :class="{ 'with-consult': showConsultCard }">
      <ConsultBookingCard v-if="showConsultCard" :projectId="projectId" />
      <PendingStatus v-if="projectStore.isPending" />
      <ProcessingStatus
        v-else-if="projectStore.isProcessing && !uiStore.selectedItem"
        :frameworks="frameworks"
        :debugMode="debugMode"
      />
      <ContentArea v-else />
    </main>
  </div>

  <div v-if="projectStore.error && !projectStore.isProcessing" class="error-toast">
    {{ projectStore.error }}
  </div>

  <div v-if="projectStore.retryToast.visible" class="retry-toast">
    {{ projectStore.retryToast.message }}
  </div>

  <div v-if="projectStore.incompleteOutputs.length" class="incomplete-notice">
    <strong>{{ incompleteLabel }} still finishing.</strong>
    We hit a snag part way through and are picking up where we left off. What is here is ready to read.
  </div>

  <div v-if="toastMessage" class="success-toast">
    {{ toastMessage }}
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ConsultBookingCard from '../components/ConsultBookingCard.vue';
import ContentArea from '../components/ContentArea.vue';
import PendingStatus from '../components/PendingStatus.vue';
import ProcessingStatus from '../components/ProcessingStatus.vue';
import TreeMenu from '../components/TreeMenu.vue';
import { useProfileSync } from '../composables/useProfileSync';
import { buildOutputUrl, parseOutputUrl } from '../lib/urlUtils';
import { useProjectStore } from '../stores/project';
import { useUIStore } from '../stores/ui';

const route = useRoute();
const router = useRouter();
const projectStore = useProjectStore();
const uiStore = useUIStore();
const { syncDisplayName } = useProfileSync();

const projectId = computed(() => route.params['projectId'] as string);
const debugMode = ref<boolean>(false);
const toastMessage = ref<string>('');
let toastTimeout: ReturnType<typeof setTimeout> | null = null;

const frameworks = computed(() => projectStore.frameworks);

// Author Nation booking card: the card itself checks the roast came through the event link
const showConsultCard = computed(
  () =>
    projectStore.isOwner &&
    projectStore.isComplete &&
    projectStore.frameworks.some(f => f.frameworkId === 'chapter-roast')
);

// "Your novel chapter notes" / "Two sections", depending on how many came up short.
const incompleteLabel = computed(() => {
  const pending = projectStore.incompleteOutputs;
  const only = pending.length === 1 ? pending[0] : undefined;
  if (only) return `Your ${only.label} are`;
  return `${pending.length} sections are`;
});

// Mobile responsiveness
const isMobile = ref(window.innerWidth < 768);
const isMobileMenuOpen = ref(false);

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
}

function handleResize() {
  isMobile.value = window.innerWidth < 768;
  if (!isMobile.value) {
    isMobileMenuOpen.value = false;
  }
}

function showToast(message: string) {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastMessage.value = message;
  toastTimeout = setTimeout(() => {
    toastMessage.value = '';
  }, 3000);
}

// Update document title when title/author changes
watch(
  [() => projectStore.title, () => projectStore.author],
  ([title, author]) => {
    if (title && author) {
      document.title = `${title} by ${author} - Twig Developmental Editor`;
    } else if (title) {
      document.title = `${title} - Twig Developmental Editor`;
    } else {
      document.title = 'Twig Developmental Editor';
    }
  },
  { immediate: true }
);

onMounted(async () => {
  // Set up resize listener for mobile responsiveness
  window.addEventListener('resize', handleResize);

  try {
    // Sync displayName from database if not set in Firebase Auth
    // This handles the case where user just completed checkout and Stripe provided their name
    syncDisplayName();

    // Load project data (includes isPublic)
    await projectStore.load(projectId.value);

    // Redirect to first output if edit is complete and no file is selected
    redirectToFirstOutputIfNeeded();

    // Parse URL for selected file (will be called again when frameworks load)
    parseUrlForSelectedFile();
  } catch (err: any) {
    console.error('Failed to load project:', err);
  }
});

onMounted(() => {
  // Add keyboard shortcut listener for admin debug mode
  const handleKeyDown = (event: KeyboardEvent) => {
    // Ctrl+Shift+D (cross-platform)
    if (event.ctrlKey && event.shiftKey && event.code === 'KeyD') {
      event.preventDefault();
      toggleDebugMode();
    }
  };

  window.addEventListener('keydown', handleKeyDown);

  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  projectStore.cleanup();
});

// Toggle debug mode and fetch debug info
async function toggleDebugMode() {
  if (!debugMode.value) {
    const success = await projectStore.loadDebugInfo();
    if (success) {
      debugMode.value = true;
      console.log('🔧 Admin debug mode enabled - additional cost and intermediate data loaded');
    } else {
      alert('Failed to load debug information. Make sure you have admin access.');
    }
  } else {
    debugMode.value = false;
    console.log('🔧 Admin debug mode disabled');
  }
}

// Handle force stop - reload project to get updated state
async function handleForceStop() {
  console.log('🛑 Processing force stopped - reloading project');
  debugMode.value = false;
  await projectStore.load(projectId.value);
}

// Handle recreation started - reload project to reconnect WebSocket
async function handleRecreateStarted() {
  console.log('🔄 Recreation started - reloading project');
  debugMode.value = false; // Exit debug mode since we're going back to processing
  await projectStore.load(projectId.value);
}

// Redirect to first output if edit is complete and no file is selected
function redirectToFirstOutputIfNeeded() {
  // Dashboard is now the default view when no output is selected — do nothing.
}

// Parse URL for selected file
async function parseUrlForSelectedFile() {
  const frameworkId = route.params['frameworkId'] as string | undefined;
  const fileName = route.params['fileName'] as string | undefined;
  const outputName = route.params['outputName'] as string | undefined;

  let outputKey: string | undefined;

  if (frameworkId && fileName) {
    // New format: /edit/:projectId/:frameworkId/:fileName
    if (frameworkId === 'system') {
      // Special system files (manuscript, costs)
      outputKey = fileName;
    } else {
      // Regular framework file: reconstruct prefixed name using utility
      const parsed = parseOutputUrl(frameworkId, fileName);
      outputKey = parsed.outputName;
    }
  } else if (outputName) {
    // Old format or generic redirect: /edit/:projectId/:outputName
    outputKey = outputName;

    // Check if it's a generic file name (no framework prefix)
    const hasFrameworkPrefix = [
      'novel_',
      'roast_',
      'short_story_',
      'memoir_',
      'non_fiction_',
      'story_grid_',
      'query_letter_',
    ].some(prefix => outputName.startsWith(prefix));

    if (!hasFrameworkPrefix && outputName !== 'manuscript' && outputName !== 'costs') {
      // Generic redirect: find main framework
      const mainFramework = projectStore.frameworks.find(
        f => f.frameworkId !== 'roast' && f.frameworkId !== 'query-letter'
      );
      if (mainFramework) {
        const targetOutput = `${mainFramework.frameworkId.replace(/-/g, '_')}_${outputName}`;
        const hasOutput = mainFramework.outputs.some(o => o.name === targetOutput);
        if (hasOutput) {
          // Redirect to nested URL using utility
          const url = buildOutputUrl(projectId.value, mainFramework.frameworkId, targetOutput);
          router.replace(url);
          return;
        }
      }
    }

    // Check if it's an old-style URL (has framework prefix but flat structure)
    if (hasFrameworkPrefix) {
      // Redirect to nested format
      // Match underscored prefix from output name (e.g., "short_story" from "short_story_welcome")
      const match = outputName.match(/^(novel|roast|short_story|memoir|non_fiction|story_grid|query_letter)_(.+)$/);
      if (match && match[1] && match[2]) {
        const frameworkWithUnderscores = match[1];
        const file = match[2];
        const targetOutput = `${frameworkWithUnderscores}_${file}`;
        // Redirect using utility function
        const url = buildOutputUrl(projectId.value, frameworkWithUnderscores, targetOutput);
        router.replace(url);
        return;
      }
    }
  }

  if (!outputKey) {
    uiStore.clearSelection();
    return;
  }

  // Check if it's a special document first
  if (outputKey === 'manuscript' && projectStore.manuscript) {
    await uiStore.selectOutput('manuscript');
  } else if (outputKey === 'costs' && projectStore.costs) {
    await uiStore.selectOutput('costs');
  } else {
    await uiStore.selectOutput(outputKey);
  }
}

// Listen for URL changes and parse when route changes
watch(
  () => route.path,
  () => {
    parseUrlForSelectedFile();
  },
  { immediate: true }
);

// Re-parse URL when frameworks change
watch(
  () => projectStore.frameworks,
  () => {
    const hasOutputs = projectStore.frameworks.some(f => f.outputs.length > 0);
    if (hasOutputs) {
      parseUrlForSelectedFile();
    }
  },
  { deep: true }
);
</script>

<style scoped>
.project-layout {
  display: flex;
  max-width: 1400px;
  align-self: center;
  width: 100%;
  flex: 1;
  min-height: 0;
}

.logo-link {
  display: flex;
  font-size: 18px;
  font-weight: 500;
  height: 100%;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  color: inherit;
  transition: background-color var(--transition-fast);
  padding: 0 12px;
  box-sizing: border-box;
  overflow: hidden;
}
.logo-link:hover {
  background: rgba(0, 0, 0, 0.03);
}

.logo-image {
  height: 24px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
}

.sidebar {
  flex: 0 0 var(--sidebar-width);
  position: relative;
}

.content {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.content.with-consult > :last-child {
  flex: 1;
  min-height: 0;
}

.content {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(180deg, var(--color-bg-primary) 0%, var(--color-bg-secondary) 100%);
}

.error-toast {
  position: fixed;
  top: calc(var(--header-height) + var(--spacing-lg));
  right: var(--spacing-lg);
  background: var(--color-danger);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn var(--transition-base) ease-out;
}

.success-toast {
  position: fixed;
  top: calc(var(--header-height) + var(--spacing-lg));
  right: var(--spacing-lg);
  background: var(--color-success, #10b981);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn var(--transition-base) ease-out;
}

.incomplete-notice {
  position: fixed;
  bottom: var(--spacing-lg);
  left: 50%;
  transform: translateX(-50%);
  max-width: 34rem;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-warning, #f59e0b);
  border-left-width: 4px;
  color: var(--color-text, #1f2937);
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
}

.retry-toast {
  position: fixed;
  top: calc(var(--header-height) + var(--spacing-lg));
  right: var(--spacing-lg);
  background: var(--color-warning, #f59e0b);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn var(--transition-base) ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
